
import { useState, useCallback, useRef, useEffect } from 'react';
import type { VoiceControlConfig } from '@/components/voice-agent/VoiceControlSettings';

interface UseVoiceControlProps {
  config: VoiceControlConfig;
  onAutoStop?: () => void;
}

export const useVoiceControl = ({ config, onAutoStop }: UseVoiceControlProps) => {
  const [isListening, setIsListening] = useState(false);
  const [isPushToTalkActive, setIsPushToTalkActive] = useState(false);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSpeechTimeRef = useRef<number>(0);

  // Reset silence timeout when speech is detected
  const resetSilenceTimeout = useCallback(() => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    if (config.autoStopAfterSpeech && isListening) {
      silenceTimeoutRef.current = setTimeout(() => {
        console.log('Auto-stopping due to silence timeout');
        setIsListening(false);
        onAutoStop?.();
      }, config.autoStopTimeout * 1000);
    }
  }, [config.autoStopAfterSpeech, config.autoStopTimeout, isListening, onAutoStop]);

  // Handle speech activity
  const onSpeechStart = useCallback(() => {
    lastSpeechTimeRef.current = Date.now();
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  }, []);

  const onSpeechEnd = useCallback(() => {
    resetSilenceTimeout();
  }, [resetSilenceTimeout]);

  // Handle AI response completion
  const onAIResponseComplete = useCallback(() => {
    if (config.stopAfterAIResponse && isListening) {
      console.log('Auto-stopping after AI response');
      setIsListening(false);
    }
  }, [config.stopAfterAIResponse, isListening]);

  // Start listening based on mode
  const startListening = useCallback(() => {
    if (config.pushToTalkMode) {
      setIsPushToTalkActive(true);
      setIsListening(true);
    } else if (config.continuousMode) {
      setIsListening(true);
      resetSilenceTimeout();
    }
  }, [config.pushToTalkMode, config.continuousMode, resetSilenceTimeout]);

  // Stop listening
  const stopListening = useCallback(() => {
    setIsListening(false);
    setIsPushToTalkActive(false);
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  }, []);

  // Push-to-talk handlers
  const handlePushToTalkStart = useCallback(() => {
    if (config.pushToTalkMode) {
      setIsPushToTalkActive(true);
      setIsListening(true);
    }
  }, [config.pushToTalkMode]);

  const handlePushToTalkEnd = useCallback(() => {
    if (config.pushToTalkMode) {
      setIsPushToTalkActive(false);
      setIsListening(false);
    }
  }, [config.pushToTalkMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, []);

  return {
    isListening,
    isPushToTalkActive,
    startListening,
    stopListening,
    handlePushToTalkStart,
    handlePushToTalkEnd,
    onSpeechStart,
    onSpeechEnd,
    onAIResponseComplete,
    resetSilenceTimeout
  };
};
